import { useCallback, useEffect, useRef, useState } from 'react'

const ACTIVITY_EVENTS = ['mousedown', 'keydown', 'touchstart', 'scroll']
const ACTIVITY_SYNC_KEY = 'lastActivity'
const SERVER_ACTIVITY_SYNC_KEY = 'lastSessionActivitySync'
const SERVER_ACTIVITY_LOCK = 'adotel-session-activity-sync'

type UseIdleWarningOptions = {
  idleTimeoutMs: number
  warningBeforeMs: number
  onExpire?: () => void
  onActivity?: () => void
}

export function useIdleWarning({
  idleTimeoutMs,
  warningBeforeMs,
  onExpire,
  onActivity
}: UseIdleWarningOptions) {
  const [showWarning, setShowWarning] = useState(false)
  const [remainingSeconds, setRemainingSeconds] = useState(0)
  const warningTimerRef = useRef<ReturnType<typeof setTimeout>>()
  const countdownRef = useRef<ReturnType<typeof setInterval>>()
  const expiryTimeRef = useRef(0)
  const warningFireTimeRef = useRef(0)
  const showWarningRef = useRef(false)
  const onExpireRef = useRef(onExpire)
  const onActivityRef = useRef(onActivity)
  const lastActivitySyncRef = useRef(0)
  const activityGenerationRef = useRef(0)
  const timerGenerationRef = useRef(0)

  useEffect(() => {
    onExpireRef.current = onExpire
  }, [onExpire])

  useEffect(() => {
    onActivityRef.current = onActivity
  }, [onActivity])

  useEffect(() => {
    showWarningRef.current = showWarning
  }, [showWarning])

  const effectiveWarning = Math.min(warningBeforeMs, idleTimeoutMs)
  const enabled = idleTimeoutMs > 0 && effectiveWarning > 0
  const activitySyncIntervalMs = Math.min(
    60_000,
    Math.max(1000, Math.floor(idleTimeoutMs / 2))
  )

  const expireIfCurrent = useCallback((generation: number) => {
    if (generation !== timerGenerationRef.current) return
    timerGenerationRef.current += 1
    if (warningTimerRef.current) clearTimeout(warningTimerRef.current)
    if (countdownRef.current) clearInterval(countdownRef.current)
    onExpireRef.current?.()
  }, [])

  const startCountdown = useCallback(
    (expiryTime: number, generation: number) => {
      if (generation !== timerGenerationRef.current) return
      expiryTimeRef.current = expiryTime
      showWarningRef.current = true
      setShowWarning(true)
      const millisecondsRemaining = expiryTime - Date.now()
      const remaining = Math.max(0, Math.ceil(millisecondsRemaining / 1000))
      setRemainingSeconds(remaining)
      if (millisecondsRemaining <= 0) {
        expireIfCurrent(generation)
        return
      }

      countdownRef.current = setInterval(() => {
        if (generation !== timerGenerationRef.current) return
        const millisecondsLeft = expiryTimeRef.current - Date.now()
        const secondsLeft = Math.max(0, Math.ceil(millisecondsLeft / 1000))
        setRemainingSeconds(secondsLeft)
        if (millisecondsLeft <= 0) expireIfCurrent(generation)
      }, 1000)
    },
    [expireIfCurrent]
  )

  const resetTimers = useCallback(() => {
    if (!enabled) return
    const generation = ++timerGenerationRef.current
    showWarningRef.current = false
    expiryTimeRef.current = 0
    setShowWarning(false)

    if (warningTimerRef.current) clearTimeout(warningTimerRef.current)
    if (countdownRef.current) clearInterval(countdownRef.current)

    const warningDelay = idleTimeoutMs - effectiveWarning
    warningFireTimeRef.current = Date.now() + warningDelay

    warningTimerRef.current = setTimeout(() => {
      startCountdown(Date.now() + effectiveWarning, generation)
    }, warningDelay)
  }, [idleTimeoutMs, effectiveWarning, enabled, startCountdown])

  const dismissWarning = useCallback(() => {
    resetTimers()
    localStorage.setItem(ACTIVITY_SYNC_KEY, Date.now().toString())
  }, [resetTimers])

  const syncServerActivity = useCallback(
    (observedAt: number, generation: number) => {
      const claimSyncWindow = () => {
        if (generation !== activityGenerationRef.current) return
        if (showWarningRef.current) return
        const storedSync = Number(
          localStorage.getItem(SERVER_ACTIVITY_SYNC_KEY)
        )
        const sharedLastSync =
          Number.isFinite(storedSync) &&
          storedSync >= 0 &&
          storedSync <= observedAt
            ? storedSync
            : 0
        const lastSync = Math.max(lastActivitySyncRef.current, sharedLastSync)
        if (observedAt - lastSync < activitySyncIntervalMs) return

        lastActivitySyncRef.current = observedAt
        localStorage.setItem(SERVER_ACTIVITY_SYNC_KEY, observedAt.toString())
        onActivityRef.current?.()
      }

      if (navigator.locks) {
        void navigator.locks
          .request(
            SERVER_ACTIVITY_LOCK,
            { ifAvailable: true },
            (lock) => lock && claimSyncWindow()
          )
          .catch(claimSyncWindow)
        return
      }

      // Compatibility fallback for browsers without Web Locks. The shared timestamp still
      // bounds each tab to one request per window; a simultaneous cross-tab race may issue one
      // extra request, which is preferable to dropping keepalives and expiring an active user.
      claimSyncWindow()
    },
    [activitySyncIntervalMs]
  )

  useEffect(() => {
    if (!enabled) return
    const activityGeneration = ++activityGenerationRef.current

    resetTimers()

    const handleActivity = () => {
      if (!showWarningRef.current) {
        const now = Date.now()
        resetTimers()
        localStorage.setItem(ACTIVITY_SYNC_KEY, now.toString())
        syncServerActivity(now, activityGeneration)
      }
    }

    const handleStorageSync = (event: StorageEvent) => {
      if (event.key === ACTIVITY_SYNC_KEY) {
        resetTimers()
      }
    }

    const handleVisibilityChange = () => {
      if (document.visibilityState !== 'visible') return
      const now = Date.now()

      if (showWarningRef.current) {
        if (expiryTimeRef.current > 0 && now >= expiryTimeRef.current) {
          setRemainingSeconds(0)
          expireIfCurrent(timerGenerationRef.current)
        }
      } else if (
        warningFireTimeRef.current > 0 &&
        now >= warningFireTimeRef.current
      ) {
        clearTimeout(warningTimerRef.current!)
        const expiryTime = warningFireTimeRef.current + effectiveWarning
        if (now >= expiryTime) {
          expireIfCurrent(timerGenerationRef.current)
        } else {
          startCountdown(expiryTime, timerGenerationRef.current)
        }
      }
    }

    ACTIVITY_EVENTS.forEach((event) =>
      document.addEventListener(event, handleActivity, { passive: true })
    )
    window.addEventListener('storage', handleStorageSync)
    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      ACTIVITY_EVENTS.forEach((event) =>
        document.removeEventListener(event, handleActivity)
      )
      window.removeEventListener('storage', handleStorageSync)
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      if (warningTimerRef.current) clearTimeout(warningTimerRef.current)
      if (countdownRef.current) clearInterval(countdownRef.current)
      timerGenerationRef.current += 1
      activityGenerationRef.current += 1
    }
  }, [
    resetTimers,
    enabled,
    effectiveWarning,
    startCountdown,
    expireIfCurrent,
    activitySyncIntervalMs,
    syncServerActivity
  ])

  return { showWarning, remainingSeconds, dismissWarning }
}
